import { api } from '@/lib/api/client';
import type { Menu } from '@/features/auth/types';
import type {
  ErrorLogOverview,
  ActiveSession,
  AiUsageReport,
  BatchOverview,
  CreateMemberRequest,
  MemberSummary,
  UpdateMemberRequest,
} from './types';

/**
 * 관리자 API 호출.
 *
 * 백엔드가 /api/admin/** 를 hasRole(ADMIN) 으로 막는다.
 * 관리자가 아니면 403 이 떨어지고, 화면이 그것을 안내 문구로 바꿔 보여준다.
 */

export async function fetchMenus(): Promise<Menu[]> {
  return api.get<Menu[]>('/admin/menus');
}

export async function fetchMembers(): Promise<MemberSummary[]> {
  return api.get<MemberSummary[]>('/admin/members');
}

export async function createMember(request: CreateMemberRequest): Promise<MemberSummary> {
  return api.post<MemberSummary>('/admin/members', request);
}

export async function updateMember(
  id: number,
  request: UpdateMemberRequest,
): Promise<MemberSummary> {
  return api.put<MemberSummary>(`/admin/members/${id}`, request);
}

export async function updateMenus(id: number, menuCodes: string[]): Promise<MemberSummary> {
  return api.put<MemberSummary>(`/admin/members/${id}/menus`, { menuCodes });
}

/** 끊긴 세션 수를 돌려준다. */
export async function resetPassword(id: number, password: string): Promise<number> {
  return api.post<number>(`/admin/members/${id}/password`, { password });
}

export async function unlockMember(id: number): Promise<MemberSummary> {
  return api.post<MemberSummary>(`/admin/members/${id}/unlock`);
}

export async function fetchSessions(): Promise<ActiveSession[]> {
  return api.get<ActiveSession[]>('/admin/sessions');
}

export async function terminateSession(sessionId: string): Promise<void> {
  await api.delete<void>(`/admin/sessions/${sessionId}`);
}

/** 그 회원의 모든 기기에서 로그아웃시킨다. 기기 분실 시 쓴다. */
export async function terminateMemberSessions(id: number): Promise<number> {
  return api.delete<number>(`/admin/members/${id}/sessions`);
}

export async function fetchAiUsage(month?: string): Promise<AiUsageReport> {
  return api.get<AiUsageReport>('/admin/ai-usage', month ? { month } : undefined);
}

/**
 * 배치 실행 이력. (V51)
 *
 * 🔴 조회만 있다. 화면에서 배치를 다시 돌리는 API 를 두지 않았다
 *   자동 종목 선별을 손으로 재실행할 수 있게 되면 결과가 마음에 안 들 때
 *   다시 돌리고 싶어진다. (CLAUDE.md 1.3절)
 */
export async function fetchBatches(
  job: string | null,
  page: number,
): Promise<BatchOverview> {
  return api.get<BatchOverview>('/admin/batches', {
    ...(job ? { job } : {}),
    page,
  });
}

/**
 * 5xx 오류 이력. (2026-08-19)
 *
 * 홈서버라 로그 파일을 보려면 SSH 로 들어가야 한다.
 * 폰으로 "지금 뭔가 터졌나" 를 확인할 수 있어야 해서 만든 화면이다.
 */
export async function fetchErrorLogs(page = 0, size = 20): Promise<ErrorLogOverview> {
  return api.get<ErrorLogOverview>(`/admin/errors?page=${page}&size=${size}`);
}
